import { z } from 'zod'
import { PaginationQuerySchema, paginated } from './common'
import { IsoDateTimeSchema, OkResponseSchema, UuidSchema } from '../primitives'
import { route } from '../route-def'

/** Communications to all approved members or to groups, with per-recipient status (AC8). */

export const COMMUNICATION_SUBJECT_MAX = 200
export const COMMUNICATION_BODY_MAX = 20_000

/** Exactly one selector: strict members reject `{ all: true, groupIds }` instead of dropping the groups. */
export const AudienceSchema = z.union([
  z.strictObject({ all: z.literal(true) }),
  z.strictObject({ groupIds: z.array(UuidSchema).min(1).max(100) }),
])
export type Audience = z.infer<typeof AudienceSchema>

const MessageFieldsSchema = z.object({
  subject: z.string().trim().min(1).max(COMMUNICATION_SUBJECT_MAX),
  body: z.string().trim().min(1).max(COMMUNICATION_BODY_MAX),
})

export const CommunicationCreateBodySchema = MessageFieldsSchema.extend({ audience: AudienceSchema })
export const CommunicationTestBodySchema = MessageFieldsSchema

/** `?all=true` or `?groupIds=<uuid>,<uuid>`, never both. */
export const AudiencePreviewQuerySchema = z
  .object({
    all: z.enum(['true', 'false']).optional(),
    groupIds: z.string().max(4000).optional(),
  })
  .refine((q) => !(q.all === 'true' && q.groupIds !== undefined), 'Choose all=true or groupIds, not both')

export const CommunicationSchema = z.object({
  id: UuidSchema,
  subject: z.string(),
  status: z.enum(['queued', 'sending', 'sent', 'failed']),
  recipientCount: z.number().int().min(0),
  counts: z.object({
    pending: z.number().int(),
    sent: z.number().int(),
    failed: z.number().int(),
    skipped: z.number().int(),
  }),
  createdAt: IsoDateTimeSchema,
  sentAt: IsoDateTimeSchema.nullable(),
})
export type Communication = z.infer<typeof CommunicationSchema>

export const RECIPIENT_ERROR_CODES = ['erased', 'unsubscribed', 'not_approved', 'smtp_recipient_refused'] as const
export type RecipientErrorCode = (typeof RECIPIENT_ERROR_CODES)[number]

export const CommunicationRecipientSchema = z.object({
  id: z.number().int(),
  memberId: UuidSchema.nullable(),
  name: z.string().nullable(),
  status: z.enum(['pending', 'sent', 'failed', 'skipped']),
  /** A code, never mail-server text (`RECIPIENT_ERROR_CODES`). */
  error: z.enum(RECIPIENT_ERROR_CODES).nullable(),
  sentAt: IsoDateTimeSchema.nullable(),
})
export type CommunicationRecipient = z.infer<typeof CommunicationRecipientSchema>

export const UnsubscribeInfoSchema = z.object({ email: z.string(), unsubscribed: z.boolean() })

export const communicationsRoutes = {
  adminCommunications: route({
    method: 'GET',
    path: '/api/admin/communications',
    auth: 'admin',
    query: PaginationQuerySchema,
    response: paginated(CommunicationSchema),
  }),
  adminCommunicationPreview: route({
    method: 'GET',
    path: '/api/admin/communications/preview',
    auth: 'admin',
    query: AudiencePreviewQuerySchema,
    response: z.object({ recipients: z.number().int().min(0) }),
  }),
  adminCommunicationCreate: route({
    method: 'POST',
    path: '/api/admin/communications',
    auth: 'admin',
    body: CommunicationCreateBodySchema,
    response: z.object({ communication: CommunicationSchema }),
  }),
  adminCommunicationTest: route({
    method: 'POST',
    path: '/api/admin/communications/test',
    auth: 'admin',
    body: CommunicationTestBodySchema,
    response: OkResponseSchema,
  }),
  adminCommunicationRecipients: route({
    method: 'GET',
    path: '/api/admin/communications/:id/recipients',
    auth: 'admin',
    params: z.object({ id: UuidSchema }),
    query: PaginationQuerySchema,
    response: paginated(CommunicationRecipientSchema),
  }),
  unsubscribeInfo: route({
    method: 'GET',
    path: '/api/public/unsubscribe/:token',
    auth: 'public',
    params: z.object({ token: z.string().min(10).max(300) }),
    response: UnsubscribeInfoSchema,
  }),
  unsubscribe: route({
    method: 'POST',
    path: '/api/public/unsubscribe/:token',
    auth: 'public',
    params: z.object({ token: z.string().min(10).max(300) }),
    response: OkResponseSchema,
  }),
}
